import React, { useState, useEffect } from 'react';
import './DocumentViewer.css';
import BankStatementViewer from './BankStatementViewer';

const DocumentViewer = ({ results, documentType, pdfCategory, previewUrls, file, frontFile, backFile }) => {
  const [fileUrl, setFileUrl] = useState(null);
  const [showFullSummary, setShowFullSummary] = useState(false);
  const [isPreviewCollapsed, setIsPreviewCollapsed] = useState(false);
  const [splitPercent, setSplitPercent] = useState(50);
  const isDragging = React.useRef(false);
  const containerRef = React.useRef(null);

  useEffect(() => {
    if (file && !file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setFileUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [file]);

  if (!results) return null;

  // Handle both response formats: {content: {...}} or direct {...}
  const content = results.content || results;

  const handleDownloadDocs = () => {
    // Build a universal text dump from whatever fields exist in content
    const lines = [];
    const add = (label, val) => { if (val) lines.push(`${label}: ${val}`); };
    lines.push('EXTRACTED DOCUMENT DATA');
    lines.push('========================');
    add('Document Type', documentType);
    add('PDF Category', pdfCategory);
    lines.push('');
    // Identity docs
    add('Full Name', content.full_name || content.name);
    add('Father\'s Name', content.fathers_name || content.father_name);
    add('Date of Birth', content.date_of_birth || content.dob);
    add('Gender', content.gender);
    add('PAN Number', content.pan_number);
    add('Aadhaar Number', content.aadhaar_number);
    add('VID', content.vid);
    add('License Number', content.license_number || content.dl_number);
    add('Issue Date', content.issue_date || content.doi);
    add('Valid Until', content.valid_until || content.valid_till);
    add('Blood Group', content.blood_group);
    add('Vehicle Classes', content.vehicle_classes);
    add('EPIC Number', content.epic_number);
    add('Polling Station', content.polling_station);
    add('Address', content.address);
    // Resume
    add('Email', content.email);
    add('Phone', content.phone);
    add('Location', content.location);
    add('ATS Score', content.ats_score ? `${content.ats_score}%` : null);
    if (content.skills?.length) lines.push(`Skills: ${content.skills.join(', ')}`);
    if (content.experience?.length) {
      lines.push('\nExperience:');
      content.experience.forEach((e, i) => lines.push(`  ${i+1}. ${e.title} at ${e.company} (${e.duration})`));
    }
    if (content.education?.length) {
      lines.push('\nEducation:');
      content.education.forEach((e, i) => lines.push(`  ${i+1}. ${e.degree} - ${e.institution} (${e.year})`));
    }
    // Notice / legal
    add('Entity Name', content.entity_name);
    add('Registration Number', content.registration_number || content.panOfClient);
    add('Document Number', content.document_number);
    add('Document Date', content.document_date);
    add('Relevant Period', content.relevant_period);
    add('Forum/Authority', Array.isArray(content.forum_authority) ? content.forum_authority.join(', ') : content.forum_authority);
    add('Provision/Section', content.relevant_section);
    // Summary
    if (content.overall_summary) { lines.push('\nSUMMARY\n-------'); lines.push(content.overall_summary); }
    if (content.summary) { lines.push('\nSUMMARY\n-------'); lines.push(typeof content.summary === 'string' ? content.summary : JSON.stringify(content.summary)); }
    if (content.key_points?.length) { lines.push('\nKEY POINTS:'); content.key_points.forEach((p, i) => lines.push(`  ${i+1}. ${p}`)); }
    if (content.issues_raised?.length) {
      lines.push('\nISSUES RAISED:');
      content.issues_raised.forEach((issue, i) => lines.push(`  ${i+1}. ${issue.issue_description} — ₹${issue.issue_amount?.toLocaleString('en-IN') || '0'}`));
    }
    if (content.next_steps?.length) {
      lines.push('\nACTION ITEMS:');
      content.next_steps.forEach((s, i) => lines.push(`  ${i+1}. [${s.step_type}] ${s.description}${s.due_date ? ` — Due: ${s.due_date}` : ''}`));
    }
    add('\nRaw OCR Text', content.text);
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `extracted_${documentType}_${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadExcel = () => {
    // Flatten all content fields into CSV rows
    const rows = [['Field', 'Value']];
    const add = (label, val) => { if (val !== undefined && val !== null && val !== '') rows.push([label, String(val).replace(/"/g, '""')]); };
    add('Document Type', documentType);
    add('PDF Category', pdfCategory);
    add('Full Name', content.full_name || content.name);
    add('Father\'s Name', content.fathers_name || content.father_name);
    add('Date of Birth', content.date_of_birth || content.dob);
    add('Gender', content.gender);
    add('PAN Number', content.pan_number);
    add('Aadhaar Number', content.aadhaar_number);
    add('VID', content.vid);
    add('License Number', content.license_number || content.dl_number);
    add('Issue Date', content.issue_date || content.doi);
    add('Valid Until', content.valid_until || content.valid_till);
    add('Blood Group', content.blood_group);
    add('Vehicle Classes', content.vehicle_classes);
    add('EPIC Number', content.epic_number);
    add('Polling Station', content.polling_station);
    add('Address', content.address);
    add('Email', content.email);
    add('Phone', content.phone);
    add('Location', content.location);
    add('ATS Score', content.ats_score ? `${content.ats_score}%` : null);
    add('Skills', content.skills?.join(', '));
    add('Entity Name', content.entity_name);
    add('Registration Number', content.registration_number || content.panOfClient);
    add('Document Number', content.document_number);
    add('Document Date', content.document_date);
    add('Relevant Period', content.relevant_period);
    add('Forum/Authority', Array.isArray(content.forum_authority) ? content.forum_authority.join(', ') : content.forum_authority);
    add('Provision/Section', content.relevant_section);
    add('Summary', content.overall_summary || (typeof content.summary === 'string' ? content.summary : ''));
    add('Confidence', content.confidence || content.average_confidence);
    if (content.experience?.length) content.experience.forEach((e, i) => add(`Experience ${i+1}`, `${e.title} at ${e.company} (${e.duration})`));
    if (content.education?.length) content.education.forEach((e, i) => add(`Education ${i+1}`, `${e.degree} - ${e.institution} (${e.year})`));
    if (content.issues_raised?.length) content.issues_raised.forEach((issue, i) => add(`Issue ${i+1}`, `${issue.issue_description} — ₹${issue.issue_amount || 0}`));
    if (content.next_steps?.length) content.next_steps.forEach((s, i) => add(`Action ${i+1}`, `[${s.step_type}] ${s.description}`));
    if (content.key_points?.length) content.key_points.forEach((p, i) => add(`Key Point ${i+1}`, p));
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `extracted_${documentType}_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadJSON = () => {
    const jsonContent = JSON.stringify(results, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${content.entity_name || 'document'}_${content.document_date || 'data'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleEdit = () => {
    console.log('Edit Extracted Data');
  };

  const togglePreview = () => {
    setIsPreviewCollapsed(!isPreviewCollapsed);
  };

  const renderDocumentData = () => {
    switch (documentType) {
      case 'PAN':
        return renderPANData();
      case 'AADHAAR':
        return renderAadhaarData();
      case 'DRIVING':
        return renderDrivingLicenseData();
      case 'VOTER':
        return renderVoterIDData();
      case 'PDF':
        if (pdfCategory === 'RESUME') return renderResumeData();
        if (pdfCategory === 'NOTICEAI') return renderNoticeData();
        return renderGenericPDFData();
      default:
        return null;
    }
  };

  const renderPANData = () => (
    <>
      {content.overall_summary && (
        <DataSection title="Executive Summary" icon="📝" priority>
          <div className="summary-actions">
            <button className="action-icon-btn" onClick={handleDownloadJSON} title="Export as JSON">
              <span className="icon">🔗</span>
            </button>
            <button className="action-icon-btn" onClick={handleDownloadDocs} title="Export as Document">
              <span className="icon">📄</span>
            </button>
            <button className="action-icon-btn" onClick={handleDownloadExcel} title="Export as CSV">
              <span className="icon">📊</span>
            </button>
            <button className="action-icon-btn" onClick={handleEdit} title="Edit Extracted Data">
              <span className="icon">✏️</span>
            </button>
            <button className="action-icon-btn" onClick={togglePreview} title={isPreviewCollapsed ? "Show Preview" : "Hide Preview"}>
              <span className="icon">{isPreviewCollapsed ? '⬅️' : '➡️'}</span>
            </button>
          </div>
          <div className="summary-box">
            <div className="summary-text">{content.overall_summary}</div>
          </div>
        </DataSection>
      )}
      <DataSection title="Personal Information" icon="👤">
        <DataField label="Full Name" value={content.full_name || content.name} />
        <DataField label="Father's Name" value={content.fathers_name || content.father_name} />
        <DataField label="Date of Birth" value={content.date_of_birth || content.dob} isDate />
      </DataSection>
      <DataSection title="PAN Details" icon="🆔">
        <DataField label="PAN Number" value={content.pan_number} highlight />
        <DataField label="Document Type" value={content.document_type} />
      </DataSection>
      {content.text && (
        <DataSection title="Extracted Text" icon="📄">
          <DataField label="Raw OCR Text" value={content.text} multiline />
        </DataSection>
      )}
      {content.confidence && (
        <DataSection title="Quality Metrics" icon="📊">
          <DataField label="Confidence Score" value={`${content.confidence.toFixed(2)}%`} />
        </DataSection>
      )}
    </>
  );

  const renderAadhaarData = () => (
    <>
      {content.overall_summary && (
        <DataSection title="Executive Summary" icon="📝" priority>
          <div className="summary-actions">
            <button className="action-icon-btn" onClick={handleDownloadJSON} title="Export as JSON">
              <span className="icon">🔗</span>
            </button>
            <button className="action-icon-btn" onClick={handleDownloadDocs} title="Export as Document">
              <span className="icon">📄</span>
            </button>
            <button className="action-icon-btn" onClick={handleDownloadExcel} title="Export as CSV">
              <span className="icon">📊</span>
            </button>
            <button className="action-icon-btn" onClick={handleEdit} title="Edit Extracted Data">
              <span className="icon">✏️</span>
            </button>
            <button className="action-icon-btn" onClick={togglePreview} title={isPreviewCollapsed ? "Show Preview" : "Hide Preview"}>
              <span className="icon">{isPreviewCollapsed ? '⬅️' : '➡️'}</span>
            </button>
          </div>
          <div className="summary-box">
            <div className="summary-text">{content.overall_summary}</div>
          </div>
        </DataSection>
      )}
      <DataSection title="Personal Information" icon="👤">
        <DataField label="Full Name" value={content.full_name} />
        <DataField label="Date of Birth" value={content.date_of_birth} isDate />
        <DataField label="Gender" value={content.gender} />
      </DataSection>
      <DataSection title="Aadhaar Details" icon="🆔">
        <DataField label="Aadhaar Number" value={content.aadhaar_number} highlight />
        <DataField label="VID" value={content.vid} />
      </DataSection>
      <DataSection title="Address" icon="📍">
        <DataField label="Full Address" value={content.address} multiline />
      </DataSection>
    </>
  );

  const renderDrivingLicenseData = () => (
    <>
      {content.overall_summary && (
        <DataSection title="Executive Summary" icon="📝" priority>
          <div className="summary-actions">
            <button className="action-icon-btn" onClick={handleDownloadJSON} title="Export as JSON">
              <span className="icon">🔗</span>
            </button>
            <button className="action-icon-btn" onClick={handleDownloadDocs} title="Export as Document">
              <span className="icon">📄</span>
            </button>
            <button className="action-icon-btn" onClick={handleDownloadExcel} title="Export as CSV">
              <span className="icon">📊</span>
            </button>
            <button className="action-icon-btn" onClick={handleEdit} title="Edit Extracted Data">
              <span className="icon">✏️</span>
            </button>
            <button className="action-icon-btn" onClick={togglePreview} title={isPreviewCollapsed ? "Show Preview" : "Hide Preview"}>
              <span className="icon">{isPreviewCollapsed ? '⬅️' : '➡️'}</span>
            </button>
          </div>
          <div className="summary-box">
            <div className="summary-text">{content.overall_summary}</div>
          </div>
        </DataSection>
      )}
      <DataSection title="Personal Information" icon="👤">
        <DataField label="Full Name" value={content.full_name || content.name} />
        <DataField label="Father's Name" value={content.father_name} />
        <DataField label="Date of Birth" value={content.date_of_birth || content.dob} isDate />
        <DataField label="Blood Group" value={content.blood_group} />
      </DataSection>
      <DataSection title="License Details" icon="🚗">
        <DataField label="License Number" value={content.license_number || content.dl_number} highlight />
        <DataField label="Issue Date" value={content.issue_date || content.doi} isDate />
        <DataField label="Valid Until" value={content.valid_until || content.valid_till} isDate />
        <DataField label="Vehicle Classes" value={content.vehicle_classes} />
      </DataSection>
      <DataSection title="Address" icon="📍">
        <DataField label="Full Address" value={content.address} multiline />
      </DataSection>
      {content.text && (
        <DataSection title="Extracted Text" icon="📄">
          <DataField label="Raw OCR Text" value={content.text} multiline />
        </DataSection>
      )}
      {content.confidence && (
        <DataSection title="Quality Metrics" icon="📊">
          <DataField label="Confidence Score" value={`${content.confidence.toFixed(2)}%`} />
        </DataSection>
      )}
    </>
  );

  const renderVoterIDData = () => (
    <>
      {content.overall_summary && (
        <DataSection title="Executive Summary" icon="📝" priority>
          <div className="summary-actions">
            <button className="action-icon-btn" onClick={handleDownloadJSON} title="Export as JSON">
              <span className="icon">🔗</span>
            </button>
            <button className="action-icon-btn" onClick={handleDownloadDocs} title="Export as Document">
              <span className="icon">📄</span>
            </button>
            <button className="action-icon-btn" onClick={handleDownloadExcel} title="Export as CSV">
              <span className="icon">📊</span>
            </button>
            <button className="action-icon-btn" onClick={handleEdit} title="Edit Extracted Data">
              <span className="icon">✏️</span>
            </button>
            <button className="action-icon-btn" onClick={togglePreview} title={isPreviewCollapsed ? "Show Preview" : "Hide Preview"}>
              <span className="icon">{isPreviewCollapsed ? '⬅️' : '➡️'}</span>
            </button>
          </div>
          <div className="summary-box">
            <div className="summary-text">{content.overall_summary}</div>
          </div>
        </DataSection>
      )}
      <DataSection title="Personal Information" icon="👤">
        <DataField label="Full Name" value={content.full_name} />
        <DataField label="Father's/Husband's Name" value={content.relative_name} />
        <DataField label="Date of Birth" value={content.date_of_birth} isDate />
        <DataField label="Gender" value={content.gender} />
      </DataSection>
      <DataSection title="Voter ID Details" icon="🗳️">
        <DataField label="EPIC Number" value={content.epic_number} highlight />
        <DataField label="Polling Station" value={content.polling_station} />
      </DataSection>
      <DataSection title="Address" icon="📍">
        <DataField label="Full Address" value={content.address} multiline />
      </DataSection>
    </>
  );

  const renderResumeData = () => (
    <>
      {content.overall_summary && (
        <DataSection title="Executive Summary" icon="📝" priority>
          <div className="summary-actions">
            <button className="action-icon-btn" onClick={handleDownloadJSON} title="Export as JSON">
              <span className="icon">🔗</span>
            </button>
            <button className="action-icon-btn" onClick={handleDownloadDocs} title="Export as Document">
              <span className="icon">📄</span>
            </button>
            <button className="action-icon-btn" onClick={handleDownloadExcel} title="Export as CSV">
              <span className="icon">📊</span>
            </button>
            <button className="action-icon-btn" onClick={handleEdit} title="Edit Extracted Data">
              <span className="icon">✏️</span>
            </button>
            <button className="action-icon-btn" onClick={togglePreview} title={isPreviewCollapsed ? "Show Preview" : "Hide Preview"}>
              <span className="icon">{isPreviewCollapsed ? '⬅️' : '➡️'}</span>
            </button>
          </div>
          <div className="summary-box">
            <div className="summary-text">{content.overall_summary}</div>
          </div>
        </DataSection>
      )}
      <DataSection title="Candidate Profile" icon="👤">
        <DataField label="Full Name" value={content.full_name} />
        <DataField label="Email" value={content.email} />
        <DataField label="Phone" value={content.phone} />
        <DataField label="Location" value={content.location} />
      </DataSection>
      
      {content.ats_score && (
        <DataSection title="ATS Compatibility Score" icon="🎯">
          <div className="ats-score-container">
            <div className="score-label">Match Score</div>
            <div className="score-bar">
              <div className="score-fill" style={{ width: `${content.ats_score}%` }}>
                <span className="score-text">{content.ats_score}%</span>
              </div>
            </div>
          </div>
        </DataSection>
      )}

      {content.skills && content.skills.length > 0 && (
        <DataSection title="Technical Skills" icon="💼">
          <div className="tags-container">
            {content.skills.map((skill, idx) => (
              <span key={idx} className="tag skill-tag">{skill}</span>
            ))}
          </div>
        </DataSection>
      )}

      {content.experience && content.experience.length > 0 && (
        <DataSection title="Professional Experience" icon="💼">
          {content.experience.map((exp, idx) => (
            <div key={idx} className="experience-item">
              <div className="exp-title">{exp.title}</div>
              <div className="exp-company">{exp.company}</div>
              <div className="exp-duration">{exp.duration}</div>
            </div>
          ))}
        </DataSection>
      )}

      {content.education && content.education.length > 0 && (
        <DataSection title="Academic Qualifications" icon="🎓">
          {content.education.map((edu, idx) => (
            <div key={idx} className="education-item">
              <div className="edu-degree">{edu.degree}</div>
              <div className="edu-institution">{edu.institution}</div>
              <div className="edu-year">{edu.year}</div>
            </div>
          ))}
        </DataSection>
      )}
    </>
  );

  const renderNoticeData = () => {
    const summaryText = content.overall_summary || '';
    const shouldTruncate = summaryText.length > 200;
    const displaySummary = shouldTruncate && !showFullSummary 
      ? summaryText.substring(0, 200) + '...' 
      : summaryText;

    return (
      <>
        {content.overall_summary && (
          <DataSection title="Executive Summary" icon="📝" priority>
            <div className="summary-actions">
              <button className="action-icon-btn" onClick={handleDownloadJSON} title="Export as JSON">
                <span className="icon">🔗</span>
              </button>
              <button className="action-icon-btn" onClick={handleDownloadDocs} title="Export as Document">
                <span className="icon">📄</span>
              </button>
              <button className="action-icon-btn" onClick={handleDownloadExcel} title="Export as CSV">
                <span className="icon">📊</span>
              </button>
              <button className="action-icon-btn" onClick={handleEdit} title="Edit Extracted Data">
                <span className="icon">✏️</span>
              </button>
              <button className="action-icon-btn" onClick={togglePreview} title={isPreviewCollapsed ? "Show Preview" : "Hide Preview"}>
                <span className="icon">{isPreviewCollapsed ? '⬅️' : '➡️'}</span>
              </button>
            </div>
            <div className="summary-box">
              <div className="summary-text">{displaySummary}</div>
              {shouldTruncate && (
                <button 
                  className="read-more-btn" 
                  onClick={() => setShowFullSummary(!showFullSummary)}
                >
                  {showFullSummary ? '▲ Show Less' : '▼ Read More'}
                </button>
              )}
            </div>
          </DataSection>
        )}

        <DataSection title="Entity Information" icon="📋">
          <DataField label="Entity Name" value={content.entity_name} />
          <DataField label="Address" value={content.address} multiline />
          <DataField label="Registration Number" value={content.registration_number || content.panOfClient} highlight />
          <DataField label="Document Type" value={content.document_type} />
          <DataField label="Document Number" value={content.document_number} />
          <DataField label="Document Date" value={content.document_date} isDate />
          <DataField label="Description" value={content.description} multiline />
          <DataField label="Relevant Period" value={content.relevant_period} />
          <DataField label="Forum/Authority" value={Array.isArray(content.forum_authority) ? content.forum_authority.join(', ') : content.forum_authority} multiline />
          <DataField label="Provision/Section" value={content.relevant_section} />
        </DataSection>

        {content.issues_raised && content.issues_raised.length > 0 && (
          <DataSection title="Identified Issues" icon="⚠️">
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{width: '50px', textAlign: 'center'}}>S.No</th>
                    <th style={{textAlign: 'center'}}>Description</th>
                    <th style={{width: '120px', textAlign: 'center'}}>Amount (INR)</th>
                  </tr>
                </thead>
                <tbody>
                  {content.issues_raised.map((issue, idx) => (
                    <tr key={idx}>
                      <td style={{textAlign: 'center'}}>{idx + 1}</td>
                      <td className="issue-desc" title={issue.issue_description}>
                        {issue.issue_description}
                      </td>
                      <td className="amount">{issue.issue_amount?.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DataSection>
        )}

        {content.information_requested && content.information_requested.length > 0 && (
          <DataSection title="Required Information" icon="📋">
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{width: '50px', textAlign: 'center'}}>S.No</th>
                    <th style={{textAlign: 'center'}}>Description</th>
                    <th style={{width: '100px', textAlign: 'center'}}>Due Date</th>
                    <th style={{width: '120px', textAlign: 'center'}}>Forum</th>
                  </tr>
                </thead>
                <tbody>
                  {content.information_requested.map((info, idx) => (
                    <tr key={idx}>
                      <td style={{textAlign: 'center'}}>{idx + 1}</td>
                      <td>{typeof info === 'string' ? info : info.description}</td>
                      <td style={{textAlign: 'center'}}>{info.due_date || '-'}</td>
                      <td style={{textAlign: 'center'}}>{info.forum || content.forum_authority || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DataSection>
        )}

        {content.next_steps && content.next_steps.length > 0 && (
          <DataSection title="Action Items" icon="📋">
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{width: '50px', textAlign: 'center'}}>S.No</th>
                    <th style={{width: '140px', textAlign: 'center'}}>Step Type</th>
                    <th style={{textAlign: 'center'}}>Description</th>
                    <th style={{width: '100px', textAlign: 'center'}}>Due Date</th>
                    <th style={{width: '120px', textAlign: 'center'}}>Forum</th>
                  </tr>
                </thead>
                <tbody>
                  {content.next_steps.map((step, idx) => (
                    <tr key={idx}>
                      <td style={{textAlign: 'center'}}>{idx + 1}</td>
                      <td>{step.step_type}</td>
                      <td>{step.description}</td>
                      <td style={{textAlign: 'center'}}>{step.due_date || '-'}</td>
                      <td style={{textAlign: 'center'}}>{step.forum || content.forum_authority || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DataSection>
        )}

        {content.interest && (content.interest.interest_amount > 0 || content.interest.relevant_sections !== 'N/A') && (
          <DataSection title="Interest Calculation" icon="💰">
            <DataField label="Interest Amount" value={`₹ ${content.interest.interest_amount?.toLocaleString('en-IN') || '0'}`} />
            <DataField label="Relevant Sections" value={content.interest.relevant_sections} />
          </DataSection>
        )}

        {content.penalty && (content.penalty.penalty_amount > 0 || content.penalty.relevant_sections !== 'N/A') && (
          <DataSection title="Penalty Assessment" icon="⚠️">
            <DataField label="Penalty Amount" value={`₹ ${content.penalty.penalty_amount?.toLocaleString('en-IN') || '0'}`} />
            <DataField label="Relevant Sections" value={content.penalty.relevant_sections} />
          </DataSection>
        )}

        {content.signature && (
          <DataSection title="Verification Status" icon="✅">
            <DataField label="Digitally Signed" value={content.signature === 'Yes' ? '✓ Yes' : 'No'} />
          </DataSection>
        )}
      </>
    );
  };

  const renderGenericPDFData = () => {
    const rawSummary = content.summary || '';
    const summaryText = typeof rawSummary === 'string' ? rawSummary : JSON.stringify(rawSummary);
    const shouldTruncate = summaryText.split(' ').length > 150;
    const displaySummary = shouldTruncate && !showFullSummary 
      ? summaryText.split(' ').slice(0, 150).join(' ') + '...' 
      : summaryText;

    return (
      <>
        {content.summary && (
          <DataSection title="Intelligent Summary" icon="🤖" priority>
            <div className="summary-actions">
              <button className="action-icon-btn" onClick={handleDownloadJSON} title="Export as JSON">
                <span className="icon">🔗</span>
              </button>
              <button className="action-icon-btn" onClick={togglePreview} title={isPreviewCollapsed ? "Show Preview" : "Hide Preview"}>
                <span className="icon">{isPreviewCollapsed ? '⬅️' : '➡️'}</span>
              </button>
            </div>
            <div className="summary-box">
              <div className="summary-stats">
                <span className="stat-badge">📊 {content.summary_word_count || 0} words</span>
                <span className="stat-badge">📄 {content.word_count || 0} total words</span>
                {content.compression_ratio && (
                  <span className="stat-badge">⚡ {content.compression_ratio} compression</span>
                )}
              </div>
              <div className="summary-text">{displaySummary}</div>
              {shouldTruncate && (
                <button 
                  className="read-more-btn" 
                  onClick={() => setShowFullSummary(!showFullSummary)}
                >
                  {showFullSummary ? '▲ Show Less' : '▼ Read More'}
                </button>
              )}
            </div>
          </DataSection>
        )}

        {content.key_topics && content.key_topics.length > 0 && (
          <DataSection title="Topic Analysis" icon="🏷️">
            <div className="tags-container">
              {content.key_topics.map((topic, idx) => (
                <span key={idx} className="tag topic-tag">{topic}</span>
              ))}
            </div>
          </DataSection>
        )}

        {content.key_points && content.key_points.length > 0 && (
          <DataSection title="Critical Insights" icon="💡">
            <ul className="key-points-list">
              {content.key_points.map((point, idx) => (
                <li key={idx} className="key-point-item">{point}</li>
              ))}
            </ul>
          </DataSection>
        )}

        <DataSection title="Metadata" icon="📄">
          <DataField label="Pages" value={content.pages} />
          <DataField label="Processing Engine" value={content.processing_engine} />
          <DataField label="Total Sentences" value={content.sentence_count} />
        </DataSection>
      </>
    );
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (ev) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setSplitPercent(Math.min(75, Math.max(25, pct)));
    };
    const onMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  // Bank Statement renders full-width outside split-view
  if (documentType === 'PDF' && pdfCategory === 'BANK_STATEMENT') {
    return (
      <div className="document-viewer-container">
        <BankStatementViewer results={results} file={file} />
      </div>
    );
  }

  return (
    <div className="document-viewer-container">
      <div
        ref={containerRef}
        className={`split-view ${isPreviewCollapsed ? 'preview-collapsed' : ''}`}
        style={!isPreviewCollapsed ? { gridTemplateColumns: `${splitPercent}% 6px 1fr` } : undefined}
      >
        {!isPreviewCollapsed && (
          <div className="document-preview-panel">
            <div className="preview-header">
              <h4>
                <span className="preview-icon">🖼️</span>
                Source Document
              </h4>
            </div>
            <div className="preview-content">
              {documentType === 'AADHAAR' ? (
                <div className="aadhaar-preview">
                  {previewUrls.front && (
                    <div className="side-preview">
                      <h5>Front Side</h5>
                      <img src={previewUrls.front} alt="Aadhaar Front" className="document-image" />
                    </div>
                  )}
                  {previewUrls.back && (
                    <div className="side-preview">
                      <h5>Back Side</h5>
                      <img src={previewUrls.back} alt="Aadhaar Back" className="document-image" />
                    </div>
                  )}
                  {!previewUrls.front && !previewUrls.back && (
                    <div className="no-preview">
                      <div className="preview-placeholder">
                        <span className="placeholder-icon">🖼️</span>
                        <p>Upload front and back images to preview</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : file && file.type === 'application/pdf' && fileUrl ? (
                <div className="iframe-preview" key={file.name}>
                  <iframe 
                    src={fileUrl} 
                    title="Document Preview"
                    className="document-iframe"
                  />
                </div>
              ) : (previewUrls.file || (file && file.type && file.type.startsWith('image/'))) ? (
                <div className="single-preview">
                  <img src={previewUrls.file || (file ? URL.createObjectURL(file) : '')} alt="Document" className="document-image" />
                </div>
              ) : (
                <div className="no-preview">
                  <div className="preview-placeholder">
                    <span className="placeholder-icon">📁</span>
                    <p>Upload document to view preview</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {!isPreviewCollapsed && (
          <div className="split-drag-handle" onMouseDown={handleMouseDown}>
            <div className="drag-handle-line" />
          </div>
        )}

        <div className="extracted-data-panel">
          <div className="data-header">
            <h4>
              <span className="data-icon">🔍</span>
              Extracted Data
            </h4>
            {content.average_confidence && (
              <div className="confidence-badge">
                <span className="confidence-icon">🎯</span>
                {content.average_confidence}% Accuracy
              </div>
            )}
          </div>
          
          <div className="data-content">
            {renderDocumentData()}

            {content.timing && (
              <DataSection title="Performance Metrics" icon="⚡">
                <DataField label="Processing Time" value={`${content.timing.total_duration_seconds}s`} />
                <DataField label="Method Used" value={content.timing.method_used} />
                {content.pages && <DataField label="Total Pages" value={content.pages} />}
              </DataSection>
            )}

            <div className="action-section">
              <button className="action-btn primary" onClick={handleDownloadJSON}>
                <span className="btn-icon">💾</span>
                Download JSON
              </button>
              <button className="action-btn secondary" onClick={handleDownloadDocs}>
                <span className="btn-icon">📄</span>
                Download TXT
              </button>
              <button className="action-btn secondary" onClick={handleDownloadExcel}>
                <span className="btn-icon">📊</span>
                Download CSV
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DataSection = ({ title, icon, children, priority }) => (
  <div className={`info-section ${priority ? 'priority-section' : ''}`}>
    <h5 className="section-title">
      <span className="section-icon">{icon}</span>
      {title}
    </h5>
    <div className="section-content">
      {children}
    </div>
  </div>
);

const DataField = ({ label, value, highlight, multiline, isDate }) => {
  if (!value && value !== 0) return null;
  
  // Format date if it's a date field
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Handle DD/MM/YYYY format
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    return dateStr;
  };
  
  return (
    <div className={`field-item ${highlight ? 'highlight' : ''}`}>
      <label className="field-label">{label}</label>
      <div className="field-value">
        {isDate ? (
          <input 
            type="date" 
            value={formatDate(value)} 
            readOnly 
            className="extracted-input"
          />
        ) : multiline ? (
          <textarea 
            value={value} 
            readOnly 
            className="extracted-textarea"
            rows={3}
          />
        ) : (
          <input 
            type="text" 
            value={value} 
            readOnly 
            className="extracted-input"
          />
        )}
      </div>
    </div>
  );
};

export default DocumentViewer;
